import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Ship, Plane, Mail, Phone, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { quoteAPI } from '@/lib/api';
import QuoteMessaging from '@/components/QuoteMessaging';

const QuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuote();
  }, [id]);

  const fetchQuote = async () => {
    try {
      const response = await quoteAPI.getQuote(id);
      setQuote(response.data);
    } catch (error) {
      toast.error('Failed to load quote');
      navigate('/buyer/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    const pdfUrl = quoteAPI.downloadPDF(id);
    window.open(pdfUrl, '_blank');
    toast.success('PDF download started');
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!quote) {
    return null;
  }

  const Icon = quote.quote_type === 'CIF' ? Ship : Plane;

  return (
    <div className="min-h-screen bg-surface py-8">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
        <Button
          data-testid="back-to-dashboard-btn"
          variant="ghost"
          onClick={() => navigate('/buyer/dashboard')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card className="p-8">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <Icon className="w-12 h-12 text-accent" />
              <div>
                <h1 data-testid="quote-heading" className="text-3xl font-chivo font-bold text-slate-900">
                  {quote.quote_type.toUpperCase()} Quote
                </h1>
                <p className="text-slate-600">Quote ID: {quote.id.substring(0, 8)}</p>
              </div>
            </div>
            <Button
              data-testid="download-pdf-btn"
              onClick={handleDownloadPDF}
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <Label>Origin Port</Label>
              <Value data-testid="origin-port">{quote.quote_data.origin_port}</Value>
            </div>
            <div>
              <Label>Destination Port</Label>
              <Value data-testid="destination-port">{quote.quote_data.destination_port}</Value>
            </div>
            <div className="md:col-span-2">
              <Label>Cargo Description</Label>
              <Value data-testid="cargo-description">{quote.quote_data.cargo_description}</Value>
            </div>
            <div>
              <Label>Weight</Label>
              <Value data-testid="weight">{quote.quote_data.weight_kg} kg</Value>
            </div>
            <div>
              <Label>Volume</Label>
              <Value data-testid="volume">{quote.quote_data.volume_cbm} CBM</Value>
            </div>
            <div>
              <Label>Incoterms</Label>
              <Value data-testid="incoterms">{quote.quote_data.incoterms}</Value>
            </div>
            {quote.quote_data.cargo_value_usd && (
              <div>
                <Label>Cargo Value</Label>
                <Value data-testid="cargo-value">${quote.quote_data.cargo_value_usd}</Value>
              </div>
            )}
          </div>

          <Card className="p-6 bg-slate-50 border-slate-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-chivo font-bold text-slate-900">Quote Summary</h3>
              <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-sm">
                {quote.status}
              </span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-600">Total Cost:</span>
                <span data-testid="total-cost" className="text-2xl font-chivo font-bold text-slate-900">
                  ${quote.total_cost_usd}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Credits Used:</span>
                <span data-testid="credits-used" className="font-medium text-accent">{quote.cost_in_credits} credits</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Created:</span>
                <span data-testid="created-date">{new Date(quote.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </Card>

          {quote.paid && (quote.buyer_contact || quote.seller_contact) && (
            <Card data-testid="contact-reveal-section" className="p-6 bg-green-50 border-green-200">
              <h3 className="text-xl font-chivo font-bold text-slate-900 mb-4">
                Contact Information Revealed
              </h3>
              <p className="text-sm text-green-800 mb-4">
                Payment confirmed! You can now contact both parties directly.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {quote.buyer_contact && (
                  <div className="bg-white rounded-sm p-4 border border-green-200">
                    <h4 className="font-chivo font-semibold text-slate-900 mb-3 flex items-center">
                      <User className="w-4 h-4 mr-2" />
                      Buyer Contact
                    </h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Name:</strong> {quote.buyer_contact.name}</p>
                      <p className="flex items-center">
                        <Mail className="w-4 h-4 mr-2 text-slate-600" />
                        {quote.buyer_contact.email}
                      </p>
                      <p className="flex items-center">
                        <Phone className="w-4 h-4 mr-2 text-slate-600" />
                        {quote.buyer_contact.phone}
                      </p>
                    </div>
                  </div>
                )}
                
                {quote.seller_contact && (
                  <div className="bg-white rounded-sm p-4 border border-green-200">
                    <h4 className="font-chivo font-semibold text-slate-900 mb-3 flex items-center">
                      <User className="w-4 h-4 mr-2" />
                      Seller Contact
                    </h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Name:</strong> {quote.seller_contact.name}</p>
                      {quote.seller_contact.company && quote.seller_contact.company !== 'N/A' && (
                        <p><strong>Company:</strong> {quote.seller_contact.company}</p>
                      )}
                      <p className="flex items-center">
                        <Mail className="w-4 h-4 mr-2 text-slate-600" />
                        {quote.seller_contact.email}
                      </p>
                      <p className="flex items-center">
                        <Phone className="w-4 h-4 mr-2 text-slate-600" />
                        {quote.seller_contact.phone}
                      </p>
                      {quote.seller_contact.country && quote.seller_contact.country !== 'Not provided' && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <p className="font-medium text-slate-700 mb-2">Location</p>
                          {quote.seller_contact.country && (
                            <p><strong>Country:</strong> {quote.seller_contact.country}</p>
                          )}
                          {quote.seller_contact.province && quote.seller_contact.province !== 'Not provided' && (
                            <p><strong>Province:</strong> {quote.seller_contact.province}</p>
                          )}
                          {quote.seller_contact.town && quote.seller_contact.town !== 'Not provided' && (
                            <p><strong>Town:</strong> {quote.seller_contact.town}</p>
                          )}
                          {quote.seller_contact.district && quote.seller_contact.district !== 'Not provided' && (
                            <p><strong>District:</strong> {quote.seller_contact.district}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}

          <QuoteMessaging quoteId={id} isPaid={quote.paid || false} />
        </Card>
      </div>
    </div>
  );
};

const Label = ({ children }) => (
  <p className="text-sm font-inter font-medium text-slate-600 mb-1">{children}</p>
);

const Value = ({ children, ...props }) => (
  <p {...props} className="text-lg font-chivo font-semibold text-slate-900">{children}</p>
);

export default QuoteDetail;